import { Alert, Box, Stack, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useNotification } from '../../../../hooks/NotificationContext';
import { fetchAssociatedIssues } from '../../../../modules/associations';
import { createIssueLinks } from '../../../../modules/jira';
import MultiSelectDropdown from '../../../../ui/primitives/MultiSelectDropdown';
import ActionButton from '../../../../ui/primitives/ActionButton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QUERY_KEYS from '../../../../query/queryKeys';
import { RefreshButton } from '../../CustomButtons';

const JiraIssueLinkage = ({
  testCaseId,
  issueKey,
  projectId,
  handleModalClose,
  type,
  setSubmitting,
}) => {
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [submittingLink, setSubmittingLink] = useState(false);
  const [error, setError] = useState(null);
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const forceRefreshRef = useRef(false);

  const {
    data: linkedIssuesOptions = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.testCase.linkedIssues, testCaseId],
    queryFn: async () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const response = await fetchAssociatedIssues(
        testCaseId,
        projectId,
        forceRefresh
      );
      return (
        response?.issues?.map((issue) => ({
          name: `${issue.key} - ${issue.summary}`,
          id: issue.key,
        })) || []
      );
    },
    enabled: !!testCaseId,
  });

  const handleRefresh = async () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({
      queryKey: [QUERY_KEYS.testCycle.linkedIssues, testCaseId],
      exact: true,
    });
  };

  const handleLinkIssues = async () => {
    setError(null);
    if (!issueKey || selectedIssues.length === 0) {
      setError('Please select issues to link.');
      return;
    }

    const issueKeys = Array.isArray(issueKey) ? issueKey : [issueKey];
    const duplicateIssues = issueKeys.filter(key => selectedIssues.includes(key));
    if (duplicateIssues.length > 0) {
      setError(`You can't link issue(s) ${duplicateIssues.join(', ')} to itself.`);
      return;
    }

    setSubmittingLink(true);
    setSubmitting(true);
    try {
      const payload = {
        issueKey,
        selectedIssues,
      };
      await createIssueLinks(payload, projectId);
      setSelectedIssues([]);
      addNotification('success', 'Issues linked successfully.');
      handleClose();
    } catch (err) {
      setError('Failed to link issues. ' + err.message);
    } finally {
      setSubmitting(false);
      setSubmittingLink(false);
    }
  };

  const handleClose = () => {
    setSelectedIssues([]);
    setError(null);
    handleModalClose();
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Typography variant="body2">
          You can link the{' '}
          <strong>
            {type === 'create' ? 'newly created issue' : 'selected issues'}
          </strong>{' '}
          ({Array.isArray(issueKey) ? issueKey.join(', ') : issueKey}) to
          existing Jira issues that are already associated with this test case.
        </Typography>
        {!isLoading && (
          <RefreshButton
            onRefresh={handleRefresh}
            isFetching={isFetching}
            showLastUpdated={false}
            buttonSize={24}
          />
        )}
      </Stack>

      {isLoading ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Fetching issues...
        </Typography>
      ) : linkedIssuesOptions.length > 0 ? (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
              Select Jira Issues to Link
            </Typography>
            <MultiSelectDropdown
              options={linkedIssuesOptions}
              selectedValues={selectedIssues}
              onChange={setSelectedIssues}
              placeholder="Choose issues to link"
              showSelectAll
              name="issues"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <ActionButton
              label={submittingLink ? 'Linking issues...' : 'Link Issues'}
              intent="primary"
              onClick={handleLinkIssues}
              disabled={submittingLink || selectedIssues.length === 0}
              loading={submittingLink}
            />
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ my: 3 }}>
          No Jira issues are currently linked to this test case.
        </Typography>
      )}
    </Box>
  );
};

export default JiraIssueLinkage;
